import React, { useEffect, useState } from 'react';
import { Card, Col, Divider, Grid, Row, Table, Typography } from 'antd';
import MainLogo from '@app/assets/images/logo.jpg';
import SecondLogo from '@app/assets/images/logo_crop.png';
import { getInvoice } from '@app/api/prImportFreeSales.api';
import { getLabInvoice, createLabTask } from '@app/api/labs.api';

import type { ColumnsType } from 'antd/es/table';

interface InvoiceProps {
  id: any;
  setCurrentBreadcrumName: React.Dispatch<React.SetStateAction<string>>;
}

const { useBreakpoint } = Grid;

const NPInvoice: React.FC<InvoiceProps> = ({ id, setCurrentBreadcrumName }) => {
  const screens = useBreakpoint();
  const isMobile = screens.xs;

  const [invoice, setInvoice] = useState<any>(null);

  useEffect(() => {
    if (id == null) return;

    const fetchInvoice = async () => {
      try {
        const data = await getInvoice(id);
        setInvoice(data.invoice);
        setCurrentBreadcrumName(`Invoice #${data.invoice.invoice_number}`);
      } catch (error) {
        console.error('Failed to fetch invoice:', error);
      }
    };

    fetchInvoice();
  }, [id]);

  if (!invoice) return null;

  // Helper function to get friendly group name
  const getGroupName = (referenceableType: string) => {
    const groupNames: { [key: string]: string } = {
      TEST: 'Lab Items',
      VACCINE: 'Lab Items',
      PRODUCT: 'Lab Items',
      IMPORT: 'Import Items',
      FOLLOWUP: 'Follow Up Items',
      EXPORT: 'Export Items',
    };
    return groupNames[referenceableType] || referenceableType;
  };

  // Helper function to get friendly subgroup name based on referenceable_type
  const getSubGroupName = (referenceableType: string, itemableType: string) => {
    // For Lab Items, use referenceable_type to determine subcategory
    if (['TEST', 'VACCINE', 'PRODUCT'].includes(referenceableType)) {
      const labSubGroups: { [key: string]: string } = {
        TEST: 'Tests',
        VACCINE: 'Vaccines',
        PRODUCT: 'Products',
      };
      return labSubGroups[referenceableType] || referenceableType;
    }

    // For Import, Follow Up, and Export Items, use itemable_type
    const itemableSubGroups: { [key: string]: string } = {
      'App\\Models\\ProductVariety': 'Products',
      'App\\Models\\ServiceCharge': 'Services',
      'App\\Models\\LabService': 'Tests', // fallback for lab services
    };
    return itemableSubGroups[itemableType] || itemableType;
  };

  const invoiceItemsGroupedByType = () => {
    // Group by referenceable_type first, then by subcategory
    const grouped: { [referenceableType: string]: { [subCategory: string]: any[] } } = {};

    invoice.invoice_lines.forEach((line: any, index: number) => {
      const referenceableType = line.referenceable_type;
      const itemableType = line.itemable_type;
      const subCategory = getSubGroupName(referenceableType, itemableType);

      if (!grouped[referenceableType]) {
        grouped[referenceableType] = {};
      }
      if (!grouped[referenceableType][subCategory]) {
        grouped[referenceableType][subCategory] = [];
      }

      grouped[referenceableType][subCategory].push({
        key: `${referenceableType}-${subCategory}-${index}`,
        itemCode: line.itemable?.code || '-',
        description: line.itemable?.name || '-',
        qty: line.qty || '-',
        price: `${line.unit_price}`,
        total: `${line.total_price}`,
      });
    });

    const dataSource: any[] = [];

    // Sort the groups to ensure consistent order
    const groupOrder = ['TEST', 'VACCINE', 'PRODUCT', 'IMPORT', 'FOLLOWUP', 'EXPORT'];
    const sortedGroups = Object.keys(grouped).sort((a, b) => {
      const aIndex = groupOrder.indexOf(a);
      const bIndex = groupOrder.indexOf(b);
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    });

    sortedGroups.forEach((referenceableType) => {
      const groupName = getGroupName(referenceableType);

      // Add main group header
      dataSource.push({
        key: `main-header-${referenceableType}`,
        itemCode: groupName,
        referenceableType,
        isMainHeader: true,
      });

      // Define the desired order for subcategories within each group
      let subCategoryOrder: string[] = [];
      if (['TEST', 'VACCINE', 'PRODUCT'].includes(referenceableType)) {
        // For Lab Items, show Tests, Products, Vaccines in that order
        subCategoryOrder = ['Tests', 'Products', 'Vaccines'];
      } else {
        // For Import, Follow Up, Export Items, show Products, Services in that order
        subCategoryOrder = ['Products', 'Services'];
      }

      // Add subgroups in the desired order
      subCategoryOrder.forEach((subCategory) => {
        if (grouped[referenceableType][subCategory]) {
          const lines = grouped[referenceableType][subCategory];

          // Add subgroup header
          dataSource.push({
            key: `sub-header-${referenceableType}-${subCategory}`,
            itemCode: subCategory,
            referenceableType,
            subCategory,
            isSubHeader: true,
          });

          // Add items
          dataSource.push(...lines);
        }
      });
    });

    return dataSource;
  };

  const invoiceItems = invoice.invoice_lines.map((line: any, index: number) => ({
    key: index.toString(),
    itemCode: line.itemable?.code || '-',
    description: line.description || '-',
    qty: line.qty || '-',
    price: `LKR ${line.unit_price}`,
    total: `LKR ${line.total_price}`,
  }));

  const columns: ColumnsType<any> = [
    {
      title: 'Item Code',
      dataIndex: 'itemCode',
      key: 'itemCode',
      render: (text: any, record: any) => {
        if (record.isMainHeader) {
          return {
            children: <strong style={{ fontSize: '16px' }}>{text}</strong>,
            props: { colSpan: 5, style: { padding: '12px' } },
          };
        }
        if (record.isSubHeader) {
          return {
            children: <strong style={{ fontSize: '14px', color: '#595959', paddingLeft: '20px' }}>{text}</strong>,
            props: { colSpan: 5, style: { background: '#f5f5f5', padding: '8px' } },
          };
        }
        if (record.isTotal) return { children: null, props: { colSpan: 1 } };
        return <span style={{ paddingLeft: '40px' }}>{text}</span>;
      },
    },
    {
      title: 'Item Description',
      dataIndex: 'description',
      key: 'description',
      render: (text: any, record: any) =>
        record.isMainHeader || record.isSubHeader || record.isTotal ? { children: null, props: { colSpan: 0 } } : text,
    },
    {
      title: 'QTY',
      dataIndex: 'qty',
      key: 'qty',
      render: (text: any, record: any) =>
        record.isMainHeader || record.isSubHeader || record.isTotal ? { children: null, props: { colSpan: 0 } } : text,
    },
    {
      title: 'Price (LKR)',
      dataIndex: 'price',
      key: 'price',
      align: 'right',
      render: (text: any, record: any) => {
        if (record.isTotal) return { children: <strong>{text}</strong>, props: {} };
        return record.isMainHeader || record.isSubHeader ? { children: null, props: { colSpan: 0 } } : text;
      },
    },
    {
      title: 'Total (LKR)',
      dataIndex: 'total',
      key: 'total',
      align: 'right',
      render: (text: any, record: any) => {
        if (record.isTotal) return { children: <strong>{text}</strong>, props: {} };
        return record.isMainHeader || record.isSubHeader ? { children: null, props: { colSpan: 0 } } : text;
      },
    },
  ];

  const formatDate = (date: string) => {
    const newDate = new Date(date);
    return newDate.toLocaleString('sv-SE');
  };

  return (
    <div id="invoice-content">
      <Card
        style={{
          borderRadius: '16px',
          padding: '20px',
          textAlign: 'center',
          margin: 'auto',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <img src={MainLogo} alt="Main Logo" style={{ height: isMobile ? '60px' : '80px' }} />
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: isMobile ? 'center' : 'space-between',
            alignItems: 'center',
            marginTop: '30px',
          }}
        >
          <div style={{ flex: isMobile ? 'none' : '1', textAlign: isMobile ? 'center' : 'left' }}>
            <img src={SecondLogo} alt="Second Logo" style={{ height: isMobile ? '50px' : '70px' }} />
          </div>
          <div style={{ flex: '2', textAlign: 'center' }}>
            <h2 style={{ marginTop: '30px', fontSize: isMobile ? '24px' : '40px', fontWeight: 'bold' }}>Invoice</h2>
          </div>

          {/* Invisible Spacer in Desktop Mode for Proper Centering */}
          {!isMobile && <div style={{ flex: '1' }} />}
        </div>

        <Divider style={{ backgroundColor: '#D3D3D3', marginTop: '10px' }} />

        <div style={{ paddingLeft: isMobile ? 0 : '80px', marginTop: isMobile ? '40px' : '60px' }}>
          <Row
            gutter={[64, 48]}
            style={{
              marginTop: '30px',
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
            }}
          >
            <Col
              xs={24}
              md={11}
              style={{ display: 'flex', flexDirection: 'column', alignItems: isMobile ? 'center' : 'flex-start' }}
            >
              <Typography.Text strong>Invoice Number:</Typography.Text>
              <div>{invoice?.invoice_number}</div>
            </Col>

            <Col xs={24} md={11} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Typography.Text strong>Address:</Typography.Text>
              <div style={{ whiteSpace: 'pre-line', textAlign: 'center' }}>
                {invoice?.department?.name} ,
                <br />
                {invoice?.department?.address}
              </div>
            </Col>

            <Col
              xs={24}
              md={11}
              style={{ display: 'flex', flexDirection: 'column', alignItems: isMobile ? 'center' : 'flex-start' }}
            >
              <Typography.Text strong>Date:</Typography.Text>
              <div>{formatDate(invoice.updated_at)}</div>
            </Col>

            <Col xs={24} md={11} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Typography.Text strong>Department:</Typography.Text>
              <div>{invoice.department.name}</div>
            </Col>
          </Row>
        </div>

        <Divider style={{ backgroundColor: '#D3D3D3', marginTop: isMobile ? '40px' : '60px' }} />
        <h2 style={{ marginTop: '30px', fontSize: isMobile ? '16px' : '26px', fontWeight: 'bold', color: '#A9A9A9' }}>
          {invoice.partner_type === 'App\\Models\\Customer' ? 'Customer Details' : 'Department Details'}
        </h2>
        <div style={{ paddingLeft: isMobile ? 0 : '80px', marginTop: isMobile ? '40px' : '60px' }}>
          <Row
            gutter={[64, 64]}
            style={{
              marginTop: '30px',
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
            }}
          >
            <Col
              xs={24}
              md={11}
              style={{ display: 'flex', flexDirection: 'column', alignItems: isMobile ? 'center' : 'flex-start' }}
            >
              <Typography.Text strong>Name:</Typography.Text>
              <div>{invoice.partner?.name || 'N/A'}</div>
            </Col>

            <Col xs={24} md={11} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Typography.Text strong>Address:</Typography.Text>
              <div style={{ whiteSpace: 'pre-line', textAlign: 'center' }}>{invoice.partner?.address || 'N/A'}</div>
            </Col>

            {invoice.partner_type === 'App\\Models\\Customer' && (
              <>
                <Col
                  xs={24}
                  md={11}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: isMobile ? 'center' : 'flex-start',
                  }}
                >
                  <Typography.Text strong>Email:</Typography.Text>
                  <div>{invoice.partner?.user?.email}</div>
                </Col>

                <Col
                  xs={24}
                  md={11}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  <Typography.Text strong>Contact No:</Typography.Text>
                  <div>{invoice.partner?.contact_no}</div>
                </Col>
              </>
            )}
          </Row>
        </div>

        <Divider
          style={{
            backgroundColor: '#D3D3D3',
            marginTop: isMobile ? '40px' : '60px',
            marginBottom: isMobile ? '40px' : '60px',
          }}
        />
        <Table
          columns={columns}
          dataSource={invoiceItemsGroupedByType()}
          pagination={false}
          bordered
          style={{ marginTop: '30px' }}
          summary={() => (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={4}>
                <span style={{ textAlign: 'left', fontWeight: 'bold', display: 'block' }}>Total:</span>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1} align="right">
                <span style={{ fontWeight: 'bold' }}>{invoice.total}</span>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          )}
          scroll={{ x: true }}
        />
        <Divider style={{ backgroundColor: '#D3D3D3', marginTop: isMobile ? '40px' : '60px' }} />
        <div style={{ paddingLeft: isMobile ? 0 : '80px', marginTop: isMobile ? '20px' : '30px' }}>
          <Row
            gutter={[64, 64]}
            style={{
              marginTop: '30px',
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
            }}
          >
            <Col
              xs={24}
              md={24}
              lg={8}
              style={{ display: 'flex', flexDirection: 'column', alignItems: isMobile ? 'center' : 'flex-start' }}
            >
              <Typography.Text strong>Bank:</Typography.Text>
              <div> People&apos;s Bank</div>
            </Col>
            <Col
              xs={24}
              md={24}
              lg={8}
              style={{ display: 'flex', flexDirection: 'column', alignItems: isMobile ? 'center' : 'flex-start' }}
            >
              <Typography.Text strong>Account:</Typography.Text>
              <div>0000 0000 000000</div>
            </Col>
            <Col
              xs={24}
              md={24}
              lg={8}
              style={{ display: 'flex', flexDirection: 'column', alignItems: isMobile ? 'center' : 'flex-start' }}
            >
              <Typography.Text strong>Branch:</Typography.Text>
              <div>Katunayake</div>
            </Col>
          </Row>
        </div>
        <p style={{ textAlign: 'left', marginTop: '100px' }}>
          * <span style={{ marginRight: '10px' }}></span> This is a computer generated Invoice
        </p>
      </Card>
    </div>
  );
};

export default NPInvoice;
